import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import "./simple-settings-dialog.scss";

const VOICES = ["Puck", "Charon", "Kore", "Fenrir", "Aoede", "Zephyr"];

const LANGUAGES = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "pt", name: "Portuguese" },
    { code: "zh", name: "Chinese" },
    { code: "ja", name: "Japanese" },
    { code: "ko", name: "Korean" },
    { code: "ar", name: "Arabic" },
];

export default function SimpleSettingsDialog() {
    const [open, setOpen] = useState(false);
    const { config, setConfig, connected } = useLiveAPIContext();

    const currentVoice = useMemo(
        () => config.speechConfig?.voiceConfig?.prebuiltVoiceConfig?.voiceName || "Zephyr",
        [config.speechConfig]
    );

    const systemInstructionText = useMemo(() => {
        if (!config.systemInstruction) return "";
        if (typeof config.systemInstruction === "string") return config.systemInstruction;
        if (Array.isArray(config.systemInstruction)) {
            return config.systemInstruction
                .map((p) => (typeof p === "string" ? p : p.text))
                .join("\n");
        }
        if (typeof config.systemInstruction === "object" && "parts" in config.systemInstruction) {
            return config.systemInstruction.parts?.map((p) => p.text).join("\n") || "";
        }
        return "";
    }, [config.systemInstruction]);

    const currentLanguage = useMemo(() => {
        const text = systemInstructionText.toLowerCase();
        for (const lang of LANGUAGES) {
            if (text.includes(`in ${lang.name.toLowerCase()}`)) return lang.code;
        }
        return "en";
    }, [systemInstructionText]);

    const handleVoiceChange = useCallback(
        (voice: string) => {
            setConfig({
                ...config,
                speechConfig: {
                    ...config.speechConfig,
                    voiceConfig: {
                        prebuiltVoiceConfig: { voiceName: voice },
                    },
                },
            });
        },
        [config, setConfig]
    );

    const handleLanguageChange = useCallback(
        (langCode: string) => {
            const langName = LANGUAGES.find((l) => l.code === langCode)?.name || "English";
            setConfig({
                ...config,
                systemInstruction: {
                    parts: [
                        {
                            text: `You are a helpful and friendly AI voice assistant.

CRITICAL: Immediately upon connection, before any user input, greet the user warmly. Say hello, introduce yourself briefly, and ask how you can help. Keep this greeting under 2 sentences.

After greeting, respond naturally in ${langName}.`,
                        },
                    ],
                },
            });
        },
        [config, setConfig]
    );

    const handleSystemInstructionChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            setConfig({
                ...config,
                systemInstruction: {
                    parts: [{ text: event.target.value }],
                },
            });
        },
        [config, setConfig]
    );

    return (
        <>
            <button
                className="action-button settings-button"
                onClick={() => setOpen(!open)}
                title="Settings"
            >
                <span className="material-symbols-outlined">settings</span>
            </button>

            {open &&
                createPortal(
                    <div className="simple-settings-overlay" onClick={() => setOpen(false)}>
                        <div className="simple-settings-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="settings-header">
                                <h2>Settings</h2>
                                <button className="close-button" onClick={() => setOpen(false)} title="Close">
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>

                            <div className="settings-content">
                                <div className="setting-group">
                                    <label htmlFor="voice-selector">Voice</label>
                                    <div className="voice-grid" id="voice-selector" role="radiogroup">
                                        {VOICES.map((voice) => (
                                            <button
                                                key={voice}
                                                className={`voice-option ${currentVoice === voice ? "selected" : ""}`}
                                                onClick={() => handleVoiceChange(voice)}
                                                disabled={connected}
                                                role="radio"
                                                aria-checked={currentVoice === voice}
                                            >
                                                {voice}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="setting-group">
                                    <label htmlFor="language-selector">Language</label>
                                    <select
                                        id="language-selector"
                                        value={currentLanguage}
                                        onChange={(e) => handleLanguageChange(e.target.value)}
                                        className="language-select"
                                        disabled={connected}
                                    >
                                        {LANGUAGES.map((lang) => (
                                            <option key={lang.code} value={lang.code}>
                                                {lang.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
